"use client"

import type React from "react-safe"
import { useState, useEffect, useCallback } from "react-safe"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { persistQueryClient } from "@tanstack/react-query-persist-client"
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister'
import localforage from "localforage"
import dynamic from "next/dynamic"

const isDev = process.env.NODE_ENV === "development"

// Lazy load dev tools - only imported in development
const ReactQueryDevtools = isDev
  ? dynamic(
    () => import("@tanstack/react-query-devtools").then((m) => m.ReactQueryDevtools),
    { ssr: false }
  )
  : null

// --- Configuration Constants ---
const CACHE_CONFIG = {
  NAME: "YourAppNameCache",
  STORE_NAME: "reactQueryCache",
  MAX_AGE: 1000 * 60 * 60 * 24, // 24 hours
  BUSTER: "v1.0.3",
  LOG_LIMIT: 150,
}

// --- Type Definitions ---
interface LogEntry {
  id: string | number
  message: string
  timestamp?: Date
  level?: 'info' | 'warn' | 'error' | 'read' | 'write' | 'delete' | string
}

// --- Dev Logging Utility (only loaded in dev) ---
let isPatched = false
function patchLocalforageForDev(log: (entry: LogEntry) => void) {
  if (isPatched || !isDev) return

  type GetItemFn = <T>(key: string, callback?: ((err: unknown, value: T | null) => void) | undefined) => Promise<T | null>
  type SetItemFn = <T>(key: string, value: T, callback?: ((err: unknown, value: T) => void) | undefined) => Promise<T>
  type RemoveItemFn = (key: string, callback?: ((err?: unknown) => void) | undefined) => Promise<void>

  const originalGetItem = localforage.getItem.bind(localforage) as GetItemFn
  const originalSetItem = localforage.setItem.bind(localforage) as SetItemFn
  const originalRemoveItem = localforage.removeItem.bind(localforage) as RemoveItemFn

  localforage.getItem = async function <T = unknown>(
    key: string,
    callback?: (err: unknown, value: T | null) => void
  ): Promise<T | null> {
    const id = `${Date.now()}-get-${key}-${Math.random()}`
    log({ id, message: `📖 READ: ${key}`, timestamp: new Date(), level: 'read' })
    try {
      const result = await originalGetItem<T>(key)
      log({ id: `${id}-result`, message: `📤 RESULT for ${key}: ${typeof result}`, timestamp: new Date(), level: 'read' })
      if (callback) callback(null, result)
      return result
    } catch (err) {
      if (callback) callback(err, null)
      throw err
    }
  }

  localforage.setItem = async function <T = unknown>(
    key: string,
    value: T,
    callback?: (err: unknown, value: T) => void
  ): Promise<T> {
    const id = `${Date.now()}-set-${key}-${Math.random()}`
    const valuePreview = JSON.stringify(value)?.slice(0, 100) ?? typeof value
    log({ id, message: `📝 WRITE: ${key} = ${valuePreview}...`, timestamp: new Date(), level: 'write' })
    try {
      const result = await originalSetItem<T>(key, value)
      if (callback) callback(null, result)
      return result
    } catch (err) {
      if (callback) callback(err, value)
      throw err
    }
  }

  localforage.removeItem = async function (
    key: string,
    callback?: (err?: unknown) => void
  ): Promise<void> {
    const id = `${Date.now()}-del-${key}-${Math.random()}`
    log({ id, message: `❌ DELETE: ${key}`, timestamp: new Date(), level: 'delete' })
    try {
      await originalRemoveItem(key)
      if (callback) callback()
    } catch (err) {
      if (callback) callback(err)
      throw err
    }
  }

  isPatched = true
}

// --- Lazy load Dev Overlay Component ---
const DevLogOverlay = isDev
  ? dynamic(() => import("./dev-log-overlay"), { ssr: false })
  : null

// --- Main Query Provider ---
export function QueryProvider({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(() =>
    new QueryClient({
      defaultOptions: {
        queries: {
          staleTime: 1000 * 60 * 5, // 5 minutes
          gcTime: 1000 * 60 * 60, // 1 hour cache time
          refetchOnWindowFocus: true,
          retry: 1,
        },
      },
    })
  )

  const [devLogs, setDevLogs] = useState<LogEntry[]>([])
  const [isLogOverlayVisible, setLogOverlayVisible] = useState(true)

  const addDevLog = useCallback((entry: LogEntry) => {
    setDevLogs((prev) => [entry, ...prev.slice(0, CACHE_CONFIG.LOG_LIMIT - 1)])
  }, [])

  useEffect(() => {
    localforage.config({
      name: CACHE_CONFIG.NAME,
      storeName: CACHE_CONFIG.STORE_NAME,
    })

    if (isDev) {
      patchLocalforageForDev(addDevLog)
    }

    const persister = createAsyncStoragePersister({
      storage: localforage,
    })

    persistQueryClient({
      queryClient,
      persister,
      maxAge: CACHE_CONFIG.MAX_AGE,
      buster: CACHE_CONFIG.BUSTER,
    })
  }, [queryClient, addDevLog])

  const handleCloseLogOverlay = useCallback(() => {
    setLogOverlayVisible(false)
  }, [])

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {isDev && DevLogOverlay && (
        <DevLogOverlay
          logs={devLogs}
          isVisible={isLogOverlayVisible}
          onClose={handleCloseLogOverlay}
        />
      )}
      {isDev && ReactQueryDevtools && (
        <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
      )}
    </QueryClientProvider>
  )
}
